import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { reatomTelegramAuthentication } from '@root/features/auth/telegram'
import { useTranslation } from '@root/shared/localization'
import { LogoGlass, mediaMaxMobile } from '@root/shared/ui-kit'

import { doorTokensStateAtom } from '../atoms/reatom-door-tokens'

import type { DoorToken } from '../atoms/reatom-door-tokens'

/**
 * The door, for somebody in a browser who is not signed in.
 *
 * One way through it: the bot. It knows who they are, which is the whole of
 * signing in here — no password, no name to choose, no invite code to find.
 * They come back on a link from Telegram and are either in the terminal or
 * in the queue, and the queue is the only gate.
 *
 * The desktop app's own sign-in screen is not used: on a website it renders
 * nothing and navigates back to where it came from.
 *
 * Shaped as a page rather than a card. What was here was a 420px box in the
 * middle of an empty screen with "Invite only" as its heading and a small
 * secondary button — a closed door with nothing to read. A door still has to
 * say what is behind it, so: what this is, in one statement; the one way
 * through, as the only full-width thing on the screen; and the condition
 * underneath it, where a condition belongs. The acknowledgement about terms
 * and age is deliberately not here — it belongs where somebody is actually
 * let in.
 */
/** Short money, because a row has one line and nobody reads nine digits. */
const marketCap = (value: string) => {
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount <= 0) {
    return '—'
  }
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(1)}B`
  }
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`
  }
  if (amount >= 1000) {
    return `$${Math.round(amount / 1000)}K`
  }
  return `$${Math.round(amount)}`
}

const change = (value: string) => {
  const percent = Number(value)
  if (!Number.isFinite(percent)) {
    return '—'
  }
  const rounded = Math.abs(percent) >= 100 ? Math.round(percent) : percent
  return `${percent >= 0 ? '+' : ''}${rounded.toFixed(Math.abs(percent) >= 100 ? 0 : 1)}%`
}

export const DoorScreen: React.FC = () => {
  const { t } = useTranslation()
  const [tokensState] = useAtom(doorTokensStateAtom)
  const tokens: ReadonlyArray<DoorToken> = AsyncStateN.isSuccess(tokensState)
    ? tokensState.value
    : []

  const telegram = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomTelegramAuthentication(),
    [],
  )
  const signIn = useAction(telegram.execute)

  return (
    <Root>
      <Panel>
        <Brand>
          <LogoGlass size={22} />
          <BrandName>kol.trade</BrandName>
        </Brand>

        <Content>
          <Marker>{t('access.door.title')}</Marker>
          <Headline>{t('access.door.headline')}</Headline>

          <Action
            variant="solid"
            color="primary"
            radius="rounded"
            onClick={() => signIn()}
          >
            {t('access.door.telegram')}
          </Action>

          <Copy>{t('access.door.note')}</Copy>
        </Content>

        <Footer>© {new Date().getFullYear()} kol.trade</Footer>
      </Panel>

      {/* The product's own data, which is the strongest thing to put in
          front of somebody deciding whether to ask for an invitation. Falls
          back to the mark when there is nothing to show — the feed is read
          without a session, so an outage must not leave a hole. */}
      <Stage>
        {tokens.length > 0 ? (
          <Board>
            <BoardLabel>{t('access.door.live')}</BoardLabel>
            {tokens.map((token) => (
              <Row key={token.tokenAddress}>
                <RowLogo
                  style={
                    token.logo
                      ? { backgroundImage: `url(${token.logo})` }
                      : undefined
                  }
                >
                  {token.logo ? undefined : token.symbol.slice(0, 1)}
                </RowLogo>
                <RowSymbol>{token.symbol}</RowSymbol>
                <RowCap>{marketCap(token.marketCapUsd)}</RowCap>
                <RowChange $up={Number(token.priceChangePercents) >= 0}>
                  {change(token.priceChangePercents)}
                </RowChange>
              </Row>
            ))}
          </Board>
        ) : (
          <Mark aria-hidden="true" />
        )}
      </Stage>
    </Root>
  )
}

const Root = styled.main`
  min-height: 100dvh;
  display: grid;
  grid-template-columns: minmax(420px, 44%) 1fr;
  background: var(--background-1);

  ${mediaMaxMobile(css`
    grid-template-columns: 1fr;
  `)}
`

const Panel = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 32px;
  padding: 32px 40px;

  ${mediaMaxMobile(css`
    padding: 24px 20px;
    gap: 24px;
  `)}
`

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const BrandName = styled.span`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 600;
`

const Content = styled.div`
  align-self: center;
  display: grid;
  justify-items: start;
  gap: 18px;
  max-width: 540px;
`

const Marker = styled.span`
  color: var(--primary-9);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const Headline = styled.h1`
  margin: 0;
  color: var(--text-12);
  font-size: 52px;
  line-height: 1.05;
  font-weight: 700;
  letter-spacing: -0.02em;
  white-space: pre-line;

  ${mediaMaxMobile(css`
    font-size: 38px;
  `)}
`

/* The only full-width thing on the screen, because it is the only way in. */
const Action = styled(Button)`
  width: 100%;
  height: 46px;
  margin-top: 6px;
`

const Copy = styled.p`
  margin: 0;
  color: var(--text-10);
  font-size: 13px;
  line-height: 1.6;
`

const Footer = styled.div`
  color: var(--text-9);
  font-size: 11px;
`

const Stage = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: var(--background-2);
  border-left: 1px solid var(--background-3);

  /* Two guides crossing where the mark sits. A full grid was tried first and
     read as graph paper; this reads as an instrument. */
  &::before,
  &::after {
    content: '';
    position: absolute;
  }

  &::before {
    left: 0;
    right: 0;
    top: 50%;
    border-top: 1px dashed var(--background-3);
  }

  &::after {
    top: 0;
    bottom: 0;
    left: 50%;
    border-left: 1px dashed var(--background-3);
  }

  ${mediaMaxMobile(css`
    display: none;
  `)}
`

const Board = styled.div`
  position: relative;
  display: grid;
  gap: 2px;
  width: min(380px, 100%);
  padding: 0 24px;
`

const BoardLabel = styled.div`
  margin-bottom: 10px;
  color: var(--text-9);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`

/* Hover lifts the row and nothing else: the ticker carries a market cap and
   a day's change, and `pair-info` needs a session, so there is no deeper
   figure to reveal. A tooltip promising more would be making it up. */
const Row = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  transition:
    background 120ms ease-out,
    transform 120ms ease-out;

  &:hover {
    background: var(--appearance-1);
    transform: translateX(-2px);
  }
`

const RowLogo = styled.div`
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--appearance-2);
  background-size: cover;
  background-position: center;
  color: var(--text-10);
  font-size: 10px;
  font-weight: 700;
`

const RowSymbol = styled.span`
  overflow: hidden;
  color: var(--text-12);
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowCap = styled.span`
  color: var(--text-10);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`

const RowChange = styled.span<{ $up: boolean }>`
  min-width: 62px;
  color: ${(x) => (x.$up ? 'var(--success-9)' : 'var(--danger-9)')};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  text-align: right;
`

const Mark = styled.div`
  position: relative;
  width: 132px;
  height: 132px;
  border-radius: 50%;
  background-color: var(--background-1);
  background-image: url('/kol-mark.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow:
    0 0 0 1px rgba(255, 210, 63, 0.35),
    0 0 48px 4px rgba(255, 210, 63, 0.16);
`
